import { useState, useEffect } from 'react';
import { Container, Row, Col, Alert } from 'react-bootstrap';
import { ReviewFilters, ReviewList, ReviewDetailModal, DeleteReviewModal } from './components';
import type { Review } from '../../infrastructure/ReviewClient';
import { useReview } from '../../apps/hooks/review/useReview';

export default function ReviewPage() {
  const {
    allReviews,
    error,
    isLoadingAllReviews,
    isLoadingMore,
    isLoadingSearch,
    isUpdatingReview,
    isDeletingReview,
    hasMore,
    fetchReviewsByProductId,
    loadMoreReviews,
    updateReview,
    deleteReview,
    clearError,
  } = useReview();

  const [reviews, setReviews] = useState<Review[]>([]);
  const [selectedReview, setSelectedReview] = useState<Review | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reviewToDelete, setReviewToDelete] = useState<Review | null>(null);
  const [isSearchMode, setIsSearchMode] = useState(false);

  // Update reviews when allReviews changes
  useEffect(() => {
    if (!isSearchMode) {
      setReviews(allReviews);
    }
  }, [allReviews, isSearchMode]);

  const handleSearch = async (productId?: number) => {
    if (productId) {
      setIsSearchMode(true);
      try {
        const searchResults = await fetchReviewsByProductId(productId);
        setReviews(searchResults);
      } catch (error) {
        // Error is handled by the hook
      }
    } else {
      // If no productId, show all reviews
      setIsSearchMode(false);
      setReviews(allReviews);
    }
  };

  const handleLoadMore = async () => {
    if (!isSearchMode) {
      await loadMoreReviews();
    }
  };

  const handleEditReview = (review: Review) => {
    setSelectedReview(review);
    setShowModal(true);
  };

  const handleSaveReview = async (updatedReview: Review) => {
    try {
      await updateReview(updatedReview);
      setShowModal(false);
      setSelectedReview(null);

      // Update the current reviews list
      setReviews((prev) => prev.map((review) => (review.reviewId === updatedReview.reviewId ? updatedReview : review)));
    } catch (error) {
      // Error is handled by the hook
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedReview(null);
  };

  const handleDeleteReview = (review: Review) => {
    setReviewToDelete(review);
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!reviewToDelete) {
      return;
    }

    try {
      await deleteReview(reviewToDelete.productId, reviewToDelete.reviewId);
      setShowDeleteModal(false);
      setReviewToDelete(null);

      // Update the current reviews list
      setReviews((prev) => prev.filter((review) => review.reviewId !== reviewToDelete.reviewId));
    } catch (error) {
      // Error is handled by the hook
    }
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setReviewToDelete(null);
  };

  return (
    <Container fluid className="p-4">
      <Row>
        <Col>
          <h1 className="mb-4">Review Management</h1>

          {error && (
            <Alert variant="danger" dismissible onClose={clearError}>
              {error}
            </Alert>
          )}

          <ReviewFilters onSearch={handleSearch} />

          <ReviewList
            reviews={reviews}
            onEditReview={handleEditReview}
            onDeleteReview={handleDeleteReview}
            isLoading={isLoadingAllReviews || isLoadingSearch}
            hasMore={hasMore && !isSearchMode}
            isLoadingMore={isLoadingMore}
            onLoadMore={handleLoadMore}
          />

          <ReviewDetailModal
            show={showModal}
            review={selectedReview}
            onClose={handleCloseModal}
            onSave={handleSaveReview}
            isUpdating={isUpdatingReview}
          />

          <DeleteReviewModal
            show={showDeleteModal}
            review={reviewToDelete}
            onClose={handleCloseDeleteModal}
            onConfirm={handleConfirmDelete}
            isDeleting={isDeletingReview}
          />
        </Col>
      </Row>
    </Container>
  );
}
